import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { db } from "@/api/db";
import { BookOpen, ChevronRight, Search, ArrowLeftRight, TrendingUp, RefreshCw } from "lucide-react";

const VERTENTES = [
  {
    key: "Transição",
    label: "Transição de Carreira",
    desc: "Para quem quer mudar de área e entrar na nova economia.",
    icon: ArrowLeftRight,
    iconBg: "#FEEDB4",
    iconColor: "#65541B",
    badgeBg: "#FEEDB4",
    badgeColor: "#65541B",
    gradient: "linear-gradient(135deg, #F7971E, #B2911E)",
  },
  {
    key: "Recolocação",
    label: "Recolocação Profissional",
    desc: "Para quem busca uma nova oportunidade no mercado.",
    icon: RefreshCw,
    iconBg: "#D6DCFF",
    iconColor: "#2D3280",
    badgeBg: "#D6DCFF",
    badgeColor: "#2D3280",
    gradient: "linear-gradient(135deg, #667EEA, #3B49A8)",
  },
  {
    key: "Promoção",
    label: "Promoção de Carreira",
    desc: "Para quem já está no mercado e quer subir de nível.",
    icon: TrendingUp,
    iconBg: "#B0FCEA",
    iconColor: "#003326",
    badgeBg: "#B0FCEA",
    badgeColor: "#003326",
    gradient: "linear-gradient(135deg, #00DCA4, #00664C)",
  },
];

function TrackCard({ track, vertente }) {
  return (
    <Link to={createPageUrl("TrackDetail") + `?id=${track.id}`}>
      <div className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer h-full flex flex-col"
        style={{ background: '#fff', borderRadius: 16, border: '1px solid #E5E9EA' }}>
        {track.image_url ? (
          <img src={track.image_url} alt={track.title} className="w-full h-36 object-cover" />
        ) : (
          <div className="w-full h-36 flex items-center justify-center" style={{ background: vertente.gradient }}>
            <BookOpen className="w-10 h-10 text-white opacity-50" />
          </div>
        )}
        <div className="p-5 flex flex-col flex-1">
          <h3 className="font-bold mb-1" style={{ fontFamily: 'Poppins, sans-serif', color: '#2E3233' }}>
            {track.title}
          </h3>
          {track.description && (
            <p className="text-sm line-clamp-2 flex-1 mb-3" style={{ color: '#646C6F' }}>
              {track.description}
            </p>
          )}
          <div className="flex items-center justify-end mt-auto pt-3"
            style={{ borderTop: '1px solid #E5E9EA' }}>
            <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: '#00664C' }}>
              Acessar <ChevronRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Tracks() {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => { loadTracks(); }, []);

  async function loadTracks() {
    const data = await db.entities.CareerTrack.filter({ is_published: true }, "order");
    setTracks(data);
    setLoading(false);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: '#00DCA4', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  const searchActive = search.trim().length > 0;

  const filtered = searchActive
    ? tracks.filter(t =>
        t.title?.toLowerCase().includes(search.toLowerCase()) ||
        t.description?.toLowerCase().includes(search.toLowerCase())
      )
    : null;

  return (
    <div className="max-w-5xl mx-auto space-y-8">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Poppins, sans-serif', color: '#003326' }}>
          Trilhas de Carreira
        </h1>
        <p className="mt-1 text-sm" style={{ color: '#646C6F' }}>
          Escolha sua trilha e comece a evoluir profissionalmente.
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#99A6AB' }} />
        <input
          type="text"
          placeholder="Buscar trilhas..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm outline-none"
          style={{ borderColor: '#E5E9EA', background: '#fff', color: '#2E3233' }}
          onFocus={e => e.target.style.borderColor = '#00DCA4'}
          onBlur={e => e.target.style.borderColor = '#E5E9EA'}
        />
      </div>

      {/* Search results — flat grid */}
      {searchActive && (
        filtered.length === 0 ? (
          <div className="rounded-2xl text-center py-16" style={{ background: '#fff', border: '1px solid #E5E9EA' }}>
            <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-20" style={{ color: '#00664C' }} />
            <p className="font-medium" style={{ color: '#646C6F' }}>Nenhuma trilha encontrada.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map(track => {
              const vertente = VERTENTES.find(v => v.key === track.category) || VERTENTES[0];
              return <TrackCard key={track.id} track={track} vertente={vertente} />;
            })}
          </div>
        )
      )}

      {/* Sections by vertente */}
      {!searchActive && VERTENTES.map(vertente => {
        const Icon = vertente.icon;
        const items = tracks.filter(t => t.category === vertente.key);
        return (
          <div key={vertente.key}>
            {/* Section header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: vertente.iconBg }}>
                <Icon className="w-4 h-4" style={{ color: vertente.iconColor }} />
              </div>
              <div>
                <h2 className="font-bold text-base" style={{ fontFamily: 'Poppins, sans-serif', color: '#003326' }}>
                  {vertente.label}
                </h2>
                <p className="text-xs" style={{ color: '#99A6AB' }}>{vertente.desc}</p>
              </div>
            </div>

            {items.length === 0 ? (
              <div className="rounded-2xl text-center py-10"
                style={{ background: '#fff', border: '1px solid #E5E9EA' }}>
                <p className="text-sm" style={{ color: '#99A6AB' }}>Conteúdos disponíveis em breve.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.map(track => (
                  <TrackCard key={track.id} track={track} vertente={vertente} />
                ))}
              </div>
            )}
          </div>
        );
      })}

    </div>
  );
}
